import { cleanLabel, mentionRegex } from './mentions';

// One mention as it appears in the decoded text: the `@Name` span plus the id
// it had in the original body. `key` is the occurrence's stable ordinal, so two
// occurrences with the same person id or the same label stay distinct.
export type Occurrence = {
  start: number;
  end: number;
  id: string;
  name: string;
  key: number;
};

// A mention still alive in the current text: its span and the key of the
// original occurrence it belongs to. Spans are non-overlapping and ordered.
export type LiveSpan = { start: number; end: number; key: number };

// The editor's current text plus the mentions the edits have not destroyed.
export type Snapshot = { text: string; live: LiveSpan[] };

// The browser's own account of one edit: the half-open range [a, b) of the
// draft was replaced by `inserted`. Every field is observed, never inferred
// from a final-text diff.
export type Splice = { a: number; b: number; inserted: string };

// One observed undo, kept so its redo can return only to a source that undo
// could have left. Each key is a target position the undo may have reached;
// its value holds the source positions that could have produced it.
export type RedoRelation = Map<number, number[]>;

// Scan the body once. The result is the text the editor shows and each
// mention's span in that text, so a save can put back only the occurrences the
// edits left alone. Offsets are UTF-16 code units, like textarea selection.
export function scanMentions(body: string): {
  text: string;
  occurrences: Occurrence[];
} {
  let text = '';
  const occurrences: Occurrence[] = [];
  let cursor = 0;
  for (const match of body.matchAll(mentionRegex())) {
    const start = match.index ?? 0;
    text += body.slice(cursor, start) + `@${match[2]}`;
    occurrences.push({
      start: text.length - (match[2].length + 1),
      end: text.length,
      id: match[1],
      name: match[2],
      key: occurrences.length,
    });
    cursor = start + match[0].length;
  }
  return { text: text + body.slice(cursor), occurrences };
}

export function startSnapshot(scan: {
  text: string;
  occurrences: Occurrence[];
}): Snapshot {
  return {
    text: scan.text,
    live: scan.occurrences.map(o => ({
      start: o.start,
      end: o.end,
      key: o.key,
    })),
  };
}

// The send encoder blocks a mention followed by a Unicode letter or number.
// The editor mirrors that rule after every edit, so extending a name or
// deleting a separating space demotes it here too.
const NEXT_CODE_POINT = /^[\p{L}\p{N}]/u;

const BACKWARD_DELETE: Record<string, true> = {
  deleteContentBackward: true,
  deleteWordBackward: true,
  deleteSoftLineBackward: true,
  deleteHardLineBackward: true,
};
const FORWARD_DELETE: Record<string, true> = {
  deleteContentForward: true,
  deleteWordForward: true,
  deleteSoftLineForward: true,
  deleteHardLineForward: true,
};
const DELETE_TYPES: Record<string, true> = {
  ...BACKWARD_DELETE,
  ...FORWARD_DELETE,
  deleteByCut: true,
};
// Known insertion types. An unrecognized type is never reinterpreted as an
// insertion; it falls to the conservative path.
const INSERT_TYPES: Record<string, true> = {
  insertText: true,
  insertFromPaste: true,
  insertFromPasteAsQuotation: true,
  insertLineBreak: true,
  insertParagraph: true,
  insertFromYank: true,
};
export const HISTORY_TYPES: Record<string, true> = {
  historyUndo: true,
  historyRedo: true,
};

// True when the text outside [a, b) is untouched, the one condition an exact
// splice needs. A failure means the captured range is insufficient.
export function outsideOk(
  before: string,
  after: string,
  a: number,
  b: number
): boolean {
  return (
    after.slice(0, a) === before.slice(0, a) &&
    after.slice(after.length - (before.length - b)) === before.slice(b)
  );
}

// Turn the paired before/after observations into one splice, or null when the
// range evidence does not verify. Directional deletion uses the post caret as
// the browser measured it; a word or multi-code-unit deletion keeps its real
// length.
export function resolveSplice(
  before: string,
  after: string,
  pre: { start: number; end: number },
  post: { start: number; end: number },
  inputType: string
): Splice | null {
  const inserting = inputType in INSERT_TYPES;
  const deleting = inputType in DELETE_TYPES;
  if (!inserting && !deleting) return null;
  if (post.start !== post.end) return null; // an edit leaves a collapsed caret

  if (pre.start !== pre.end) {
    // A selection was replaced or deleted.
    const a = pre.start;
    const b = pre.end;
    if (a < 0 || b > before.length) return null;
    if (!outsideOk(before, after, a, b)) return null;
    const inserted = after.slice(a, after.length - (before.length - b));
    const caret = deleting ? a : a + inserted.length;
    if (post.start !== caret) return null;
    return { a, b, inserted };
  }

  if (deleting && inputType in BACKWARD_DELETE) {
    const a = post.start;
    const b = pre.start;
    if (a > b) return null;
    if (!outsideOk(before, after, a, b)) return null;
    if (after.length !== before.length - (b - a)) return null;
    return { a, b, inserted: '' };
  }

  if (deleting && inputType in FORWARD_DELETE) {
    // A collapsed forward deletion leaves the caret where it started. A stale
    // capture whose caret moved cannot describe this edit.
    if (post.start !== pre.start || post.end !== pre.end) return null;
    const a = pre.start;
    const b = a + (before.length - after.length);
    if (b < a || b > before.length) return null;
    if (!outsideOk(before, after, a, b)) return null;
    return { a, b, inserted: '' };
  }

  if (inserting) {
    const a = pre.start;
    const b = pre.end;
    if (!outsideOk(before, after, a, b)) return null;
    const inserted = after.slice(a, after.length - (before.length - b));
    if (post.start !== a + inserted.length) return null;
    return { a, b, inserted };
  }

  return null;
}

// True when a mapped span still shows its own `@Name` and is not glued to a
// following letter or number.
export function survives(text: string, label: string, span: LiveSpan): boolean {
  if (span.start < 0 || span.end > text.length) return false;
  if (text.slice(span.start, span.end) !== label) return false;
  return !NEXT_CODE_POINT.test(text.slice(span.end));
}

// Map the live spans through one splice. A touched occurrence is dropped for
// good; spans entirely before or after the edit keep or shift their offsets.
export function transition(
  nextText: string,
  live: LiveSpan[],
  splice: Splice,
  labelAt: (key: number) => string
): LiveSpan[] {
  const { a, b, inserted } = splice;
  const delta = inserted.length - (b - a);
  const out: LiveSpan[] = [];
  for (const span of live) {
    let moved: LiveSpan | null;
    if (b > a) {
      if (span.end <= a) moved = span;
      else if (span.start >= b)
        moved = {
          start: span.start + delta,
          end: span.end + delta,
          key: span.key,
        };
      else moved = null;
    } else if (span.end <= a) {
      moved = span;
    } else if (span.start >= a) {
      moved = {
        start: span.start + inserted.length,
        end: span.end + inserted.length,
        key: span.key,
      };
    } else {
      moved = null;
    }
    if (moved && survives(nextText, labelAt(moved.key), moved)) out.push(moved);
  }
  return out;
}

// The spans every candidate history snapshot agrees on. A span only some
// candidates prove is not certified and stays plain.
export function sharedLive(candidates: Snapshot[]): LiveSpan[] {
  const [first, ...rest] = candidates;
  return first.live.filter(span =>
    rest.every(c =>
      c.live.some(
        s => s.key === span.key && s.start === span.start && s.end === span.end
      )
    )
  );
}

export const sameLive = (x: LiveSpan[], y: LiveSpan[]): boolean =>
  x.length === y.length &&
  x.every(
    (s, i) => s.start === y[i].start && s.end === y[i].end && s.key === y[i].key
  );

// Put back only the certified occurrences, right to left so an earlier span
// keeps its offset in the still-unencoded text.
export function encodeSnapshot(
  snap: Snapshot,
  occurrences: Occurrence[]
): string {
  const ordered = [...snap.live].sort((x, y) => x.start - y.start);
  let result = snap.text;
  for (let i = ordered.length - 1; i >= 0; i--) {
    const span = ordered[i];
    const occurrence = occurrences[span.key];
    result = `${result.slice(0, span.start)}<@${occurrence.id}|${cleanLabel(
      occurrence.name
    )}>${result.slice(span.end)}`;
  }
  return result;
}
